import { googleFonts } from './GoogleFontsManager.js'

class RestaurantConfigService {
  constructor() {
    this.config = null
    this.listeners = new Set()
    this.isLoading = false
    this.error = null

    this.offlineStorageKey = 'tabbly_offline_config'
    this.lastSuccessfulFetch = null
    this.isOfflineMode = false
    this.maxOfflineAge = 7 * 24 * 60 * 60 * 1000

    this.initializeOfflineMonitoring()
  }

  static getInstance() {
    if (!RestaurantConfigService.instance) {
      RestaurantConfigService.instance = new RestaurantConfigService()
    }
    return RestaurantConfigService.instance
  }

  initializeOfflineMonitoring() {
    window.addEventListener('online', () => {
      console.log('[Kiosk] Network back online')
      this.isOfflineMode = false
    })

    window.addEventListener('offline', () => {
      console.log('[Kiosk] Network offline - switching to cached configuration')
      this.isOfflineMode = true
    })

    this.isOfflineMode = !navigator.onLine
  }

  saveConfigurationToStorage(config) {
    try {
      const storageData = {
        config,
        timestamp: Date.now(),
        version: '1.0'
      }
      localStorage.setItem(this.offlineStorageKey, JSON.stringify(storageData))
      this.lastSuccessfulFetch = Date.now()
      console.log('[Kiosk] Configuration cached for offline use')
    } catch (error) {
      console.warn('[Kiosk] Failed to cache configuration:', error)
    }
  }

  loadConfigurationFromStorage() {
    try {
      const storageData = localStorage.getItem(this.offlineStorageKey)
      if (!storageData) return null

      const { config, timestamp, version } = JSON.parse(storageData)

      if (Date.now() - timestamp > this.maxOfflineAge) {
        console.warn('[Kiosk] Cached configuration too old, removing')
        localStorage.removeItem(this.offlineStorageKey)
        return null
      }

      console.log(`[Kiosk] Using cached configuration (${Math.round((Date.now() - timestamp) / (1000 * 60))} minutes old)`)
      return config
    } catch (error) {
      console.warn('[Kiosk] Failed to load cached configuration:', error)
      return null
    }
  }

  isConfigurationStale() {
    return this.lastSuccessfulFetch &&
           (Date.now() - this.lastSuccessfulFetch > 60 * 60 * 1000)
  }

  subscribe(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.config))
  }

  async loadConfiguration(restaurantId) {
    if (this.isLoading) return this.config

    this.isLoading = true
    this.error = null

    const cachedConfig = this.loadConfigurationFromStorage()

    if (this.isOfflineMode && cachedConfig) {
      console.log('[Kiosk] Using cached configuration - offline mode')
      this.config = cachedConfig
      await this.applyConfiguration()
      this.notifyListeners()
      this.isLoading = false
      return this.config
    }

    try {
      const apiUrl = window.__RUNTIME_CONFIG__?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)

      const response = await fetch(`${apiUrl}/restaurants/${restaurantId}/configuration`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 404) {
          this.config = this.getDefaultConfiguration(restaurantId)
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } else {
        const data = await response.json()
        this.config = this.transformBackendData(data)

        this.saveConfigurationToStorage(this.config)
      }

      await this.applyConfiguration()
      this.notifyListeners()
      return this.config

    } catch (error) {
      console.error('[Kiosk] Failed to load restaurant configuration:', error)
      this.error = error.message

      if (cachedConfig) {
        console.log('[Kiosk] Using cached configuration as fallback')
        this.config = cachedConfig
        await this.applyConfiguration()
        this.notifyListeners()
        return this.config
      }

      console.log('[Kiosk] Using default configuration as final fallback')
      this.config = this.getDefaultConfiguration(restaurantId)
      await this.applyConfiguration()
      this.notifyListeners()
      return this.config
    } finally {
      this.isLoading = false
    }
  }

  getDefaultConfiguration(restaurantId) {
    return {
      id: `default-${restaurantId}`,
      restaurantId,
      fontSettings: {
        primary: 'Inter',
        googleFontsEnabled: false,
        systemFontFallback: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      performance: {
        cacheConfiguration: {
          fontCacheDuration: 86400
        }
      }
    }
  }

  transformBackendData(data) {
    const kiosk = data.kioskDisplaySettings || {}

    return {
      id: data.id,
      restaurantId: data.restaurantId,
      fontSettings: {
        primary: kiosk.fontSettings?.primary || 'Inter',
        headingFont: kiosk.fontSettings?.headingFont,
        bodyFont: kiosk.fontSettings?.bodyFont,
        buttonFont: kiosk.fontSettings?.buttonFont,
        googleFontsEnabled: kiosk.fontSettings?.googleFontsEnabled ?? false,
        systemFontFallback: kiosk.fontSettings?.systemFontFallback ||
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      performance: {
        cacheConfiguration: {
          fontCacheDuration: data.performance?.cacheConfiguration?.fontCacheDuration || 86400
        }
      }
    }
  }

  async applyConfiguration() {
    if (!this.config) return

    googleFonts.setConfiguration(this.config)
    googleFonts.applyFontConfiguration()
  }

  getConfiguration() {
    return this.config
  }

  isConfigLoading() {
    return this.isLoading
  }

  getError() {
    return this.error
  }

  getOfflineStatus() {
    return {
      isOfflineMode: this.isOfflineMode,
      isOnline: navigator.onLine,
      lastSuccessfulFetch: this.lastSuccessfulFetch,
      isConfigurationStale: this.isConfigurationStale(),
      hasCachedConfig: !!this.loadConfigurationFromStorage(),
      maxOfflineAge: this.maxOfflineAge,
      cacheAge: this.getCacheAge()
    }
  }

  getCacheAge() {
    try {
      const storageData = localStorage.getItem(this.offlineStorageKey)
      if (!storageData) return null

      const { timestamp } = JSON.parse(storageData)
      return Date.now() - timestamp
    } catch {
      return null
    }
  }

  async refreshConfiguration(restaurantId) {
    if (this.isOfflineMode) {
      console.log('[Kiosk] Cannot refresh - still offline')
      return this.config
    }

    console.log('[Kiosk] Force refreshing configuration...')
    return this.loadConfiguration(restaurantId)
  }

  clearOfflineCache() {
    try {
      localStorage.removeItem(this.offlineStorageKey)
      console.log('[Kiosk] Offline cache cleared')
    } catch (error) {
      console.warn('[Kiosk] Failed to clear offline cache:', error)
    }
  }
}

export const restaurantConfig = RestaurantConfigService.getInstance()

export const useRestaurantConfig = (restaurantId) => {
  return {
    loadConfiguration: () => restaurantConfig.loadConfiguration(restaurantId),
    getConfiguration: () => restaurantConfig.getConfiguration(),
    isLoading: () => restaurantConfig.isConfigLoading(),
    getError: () => restaurantConfig.getError(),
    subscribe: (listener) => restaurantConfig.subscribe(listener)
  }
}